/**
 * src/store/modules/mfa.js
 *
 * Purpose: the `mfa-store` namespaced Vuex module — the LIVE two-factor (OTP)
 * flow. Re-authored equivalent of the deployed pro-ui `mfa-store` (LIVE only;
 * component `mfa-dialog`). No MFA path exists in the OLD build.
 *
 * VERBATIM contract (re-greppable):
 *   - endpoints `user/auth/code/verify` (resultKey "hash", skipHash) and
 *     `user/auth/code/resend`
 *   - getters: counter, errors, timer, isLoading, isResendingCode,
 *     isValidatingCode, isDialogOpen
 *   - actions: auth, resendCode, setErrors, toggleDialog, verifyCode
 *     (+ internal startMfaTimeout, clearMfaTimeout, setHash, startCountdown)
 *   - i18n error key `common.errors.mfa-session-expired`,
 *     `login.mfa.errors.empty_code`.
 * Wiring / timer bodies are re-authored.
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
 * internal impl re-authored.
 */

import { request } from '../../api/request.js';

// Short-lived-hash timeout window (ms) and resend countdown seconds. The exact
// server-side TTL is INFERRED-LOST; a 2-minute window matches the observed OTP
// dialog behaviour.
const MFA_TIMEOUT_MS = 120000;
const RESEND_COUNTDOWN_S = 30;

// Module-scoped timers (kept out of reactive state).
let mfaTimeoutId = null;
let countdownId = null;

function createInitialState() {
  return {
    hash: null, // the short-lived MFA hash from user/auth
    counter: 0, // number of OTP attempts
    errors: [],
    timer: 0, // resend countdown, seconds remaining
    isLoading: false,
    isResendingCode: false,
    isValidatingCode: false,
    isDialogOpen: false
  };
}

const state = createInitialState();

const getters = {
  counter: function (state) {
    return state.counter;
  },
  errors: function (state) {
    return state.errors;
  },
  timer: function (state) {
    return state.timer;
  },
  isLoading: function (state) {
    return state.isLoading;
  },
  isResendingCode: function (state) {
    return state.isResendingCode;
  },
  isValidatingCode: function (state) {
    return state.isValidatingCode;
  },
  isDialogOpen: function (state) {
    return state.isDialogOpen;
  }
};

const mutations = {
  setHash: function (state, hash) {
    state.hash = hash;
  },
  setErrors: function (state, errors) {
    state.errors = errors || [];
  },
  setTimer: function (state, seconds) {
    state.timer = seconds;
  },
  setLoading: function (state, value) {
    state.isLoading = !!value;
  },
  setResending: function (state, value) {
    state.isResendingCode = !!value;
  },
  setValidating: function (state, value) {
    state.isValidatingCode = !!value;
  },
  setDialog: function (state, value) {
    state.isDialogOpen = !!value;
  },
  incrementCounter: function (state) {
    state.counter += 1;
  },
  resetState: function (state) {
    const fresh = createInitialState();
    Object.keys(fresh).forEach(function (k) {
      state[k] = fresh[k];
    });
  }
};

const actions = {
  setHash: function (ctx, hash) {
    ctx.commit('setHash', hash);
  },

  setErrors: function (ctx, errors) {
    ctx.commit('setErrors', errors);
  },

  toggleDialog: function (ctx, value) {
    const open = value == null ? !ctx.state.isDialogOpen : !!value;
    ctx.commit('setDialog', open);
    if (!open) {
      ctx.commit('setErrors', []);
    }
  },

  /**
   * Verify an OTP. VERBATIM:
   *   request("user/auth/code/verify", {hash, code}, "hash", {skipErrors, skipHash})
   * returning the real session hash. On success finalises through `auth`.
   */
  verifyCode: async function (ctx, code) {
    ctx.commit('setValidating', true);
    ctx.commit('setErrors', []);
    ctx.commit('incrementCounter');
    try {
      const hash = await request(
        'user/auth/code/verify',
        { hash: ctx.state.hash, code: code },
        'hash',
        { skipErrors: true, skipHash: true }
      );
      await ctx.dispatch('auth', hash);
      return hash;
    } catch (err) {
      const code2 = err && err.errorCode;
      // stale/expired short-lived hash -> mfa-session-expired
      if (code2 === 3 || code2 === 4 || code2 === 'no_hash') {
        ctx.commit('setErrors', ['common.errors.mfa-session-expired']);
      } else {
        ctx.commit('setErrors', ['login.mfa.errors.empty_code']);
      }
      throw err;
    } finally {
      ctx.commit('setValidating', false);
    }
  },

  /**
   * Finalise a verified MFA: set the real session, load the user, close the
   * dialog and control returns to `loginAction()`.
   */
  auth: function (ctx, hash) {
    ctx.commit('setLoading', true);
    return ctx
      .dispatch('user/setSession', hash, { root: true })
      .then(function () {
        return ctx.dispatch('user/loadUserWithPlugins', null, { root: true });
      })
      .then(function () {
        ctx.dispatch('clearMfaTimeout');
        ctx.commit('setDialog', false);
      })
      .finally(function () {
        ctx.commit('setLoading', false);
      });
  },

  /**
   * Resend the OTP. VERBATIM `user/auth/code/resend` with the short-lived hash;
   * arms the resend countdown.
   */
  resendCode: function (ctx) {
    ctx.commit('setResending', true);
    return request('user/auth/code/resend', { hash: ctx.state.hash })
      .then(function () {
        ctx.dispatch('startCountdown');
      })
      .finally(function () {
        ctx.commit('setResending', false);
      });
  },

  // --- internal timers ---

  startMfaTimeout: function (ctx) {
    ctx.dispatch('clearMfaTimeout');
    mfaTimeoutId = setTimeout(function () {
      // short-lived hash expired before verification
      ctx.commit('setErrors', ['common.errors.mfa-session-expired']);
      ctx.commit('setDialog', false);
      ctx.commit('setHash', null);
    }, MFA_TIMEOUT_MS);
    return Promise.resolve();
  },

  clearMfaTimeout: function () {
    if (mfaTimeoutId) {
      clearTimeout(mfaTimeoutId);
      mfaTimeoutId = null;
    }
    return Promise.resolve();
  },

  startCountdown: function (ctx) {
    if (countdownId) {
      clearInterval(countdownId);
      countdownId = null;
    }
    ctx.commit('setTimer', RESEND_COUNTDOWN_S);
    countdownId = setInterval(function () {
      const next = ctx.state.timer - 1;
      ctx.commit('setTimer', next > 0 ? next : 0);
      if (next <= 0 && countdownId) {
        clearInterval(countdownId);
        countdownId = null;
      }
    }, 1000);
    return Promise.resolve();
  }
};

export default {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions
};

export { createInitialState };
